// src/app/api/paymentApi.ts
//
// RTK Query slice for all /api/payments/* endpoints.
// The secret key is NEVER used here — the backend owns all Paystack communication.
// The frontend receives an authorizationUrl and redirects the user to it.

import { baseApi } from "./baseApi";
import type { PayStatus, PayMethod, PayType } from "./parentApi";
import type { StudentTrack } from "./authApi";

/* ─────────────────────────────────────────────────────────────────
   PAYMENT TYPES
───────────────────────────────────────────────────────────────── */

export interface PaymentChild {
  id: string;
  firstName: string;
  lastName: string;
  age?: number;
  track?: StudentTrack;
}

export interface PaymentEnrolment {
  id?: string;
  quarter?: string;
  academicYear?: string;
  billingPeriod?: string;
  track?: StudentTrack;
  status?: string;
  feeAmount?: number;
  grossAmount?: number;
  discountPercent?: number;
  discountAmount?: number;
}

export interface Payment {
  id: string;
  description: string;
  type: PayType;
  amount: number;
  amountFormatted: string;
  currency: string;
  status: PayStatus;
  method: PayMethod;
  reference: string | null;
  authorizationUrl: string | null;
  gatewayResponse: string | null;
  dueDate: string | null;
  paidAt: string | null;
  receiptUrl: string | null;
  notes: string | null;
  child: PaymentChild | null;
  enrolment: PaymentEnrolment | string;
  parent: string;
  createdAt: string;
  updatedAt: string;
}

/* ─────────────────────────────────────────────────────────────────
   INITIALIZE RESPONSE
   The most important field is authorizationUrl — redirect here.
───────────────────────────────────────────────────────────────── */

export interface InitializePaymentResult {
  paymentId: string;
  reference: string;
  /** Redirect the user to this URL — Paystack's hosted checkout page */
  authorizationUrl: string;
  /** Used with Paystack's inline popup (optional) */
  accessCode: string;
  amount: number;         // naira
  amountKobo: number;     // kobo
  description: string;
  customer: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface VerifyPaymentResult {
  alreadyProcessed?: boolean;
  payment: {
    id: string;
    reference: string;
    status: PayStatus;
    paidAt: string | null;
    amount: number;
  };
  enrolment: Record<string, any>;
  child: { firstName: string; lastName: string } | null;
}

/* ─────────────────────────────────────────────────────────────────
   REQUEST TYPES
───────────────────────────────────────────────────────────────── */

export interface GetPaymentsParams {
  childId?: string;
  status?: PayStatus;
  page?: number;
  limit?: number;
}

export interface AdminGetPaymentsParams extends GetPaymentsParams {
  parentId?: string;
  method?: PayMethod;
  from?: string;
  to?: string;
}

export interface AdminConfirmPaymentBody {
  enrolmentId: string;
  reference: string;
  paidAt?: string;
  notes?: string;
}

export interface AdminRefundBody {
  amountNaira?: number;
  reason?: string;
}

/* ─────────────────────────────────────────────────────────────────
   RESPONSE WRAPPERS
───────────────────────────────────────────────────────────────── */

interface SingleResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ListResponse<T> {
  success: boolean;
  message: string;
  count: number;
  total: number;
  page: number;
  pages: number;
  data: T[];
}

interface AdminListResponse<T> extends ListResponse<T> {
  summary: {
    totalPaid: number;
    totalPending: number;
    count: number;
  };
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE
───────────────────────────────────────────────────────────────── */

export const paymentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // ── PARENT — initialise payment (backend calls Paystack) ──────
    /**
     * Sends enrolmentId to our backend → backend calls Paystack with
     * the secret key → returns authorizationUrl to the frontend.
     * Frontend then redirects to authorizationUrl.
     */
    initializePayment: builder.mutation<
      SingleResponse<InitializePaymentResult>,
      { enrolmentId: string }
    >({
      query: (body) => ({
        url:    "/payments/initialize",
        method: "POST",
        body,
      }),
    }),

    // ── PARENT — verify payment after Paystack redirects back ─────
    /**
     * Called after Paystack redirects to /user/payments/callback?ref=...
     * Backend verifies with Paystack API and confirms the Payment record.
     */
    verifyPayment: builder.mutation<
      SingleResponse<VerifyPaymentResult>,
      { reference: string }
    >({
      query: (body) => ({
        url:    "/payments/verify",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "ParentPayments" as const, id: "LIST" },
        { type: "Enrolments"     as const, id: "LIST" },
        "ParentDashboard" as const,
        "ParentChildren"  as const,
      ],
    }),

    // ── PARENT — list own payments ────────────────────────────────
    getPayments: builder.query<ListResponse<Payment>, GetPaymentsParams>({
      query: (params = {}) => ({ url: "/payments", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ParentPayments" as const,
                id,
              })),
              { type: "ParentPayments" as const, id: "LIST" },
            ]
          : [{ type: "ParentPayments" as const, id: "LIST" }],
    }),

    // ── PARENT — single payment ───────────────────────────────────
    getPayment: builder.query<SingleResponse<Payment>, string>({
      query: (id) => `/payments/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ParentPayments" as const, id }],
    }),

    // ── ADMIN — list all payments ─────────────────────────────────
    adminGetPayments: builder.query<AdminListResponse<Payment>, AdminGetPaymentsParams>({
      query: (params = {}) => ({ url: "/payments/admin", params }),
      providesTags: [{ type: "ParentPayments" as const, id: "ADMIN_LIST" }],
    }),

    // ── ADMIN — single payment ────────────────────────────────────
    adminGetPayment: builder.query<SingleResponse<Payment>, string>({
      query: (id) => `/payments/admin/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ParentPayments" as const, id }],
    }),

    // ── ADMIN — manually confirm bank transfer ────────────────────
    adminConfirmPayment: builder.mutation<
      SingleResponse<Payment>,
      AdminConfirmPaymentBody
    >({
      query: (body) => ({
        url:    "/payments/admin/confirm",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "ParentPayments" as const, id: "ADMIN_LIST" },
        { type: "Enrolments"     as const, id: "LIST" },
        "ParentDashboard" as const,
      ],
    }),

    // ── ADMIN — initiate Paystack refund ──────────────────────────
    adminRefundPayment: builder.mutation<
      SingleResponse<{ refundData: any; payment: Payment }>,
      { id: string } & AdminRefundBody
    >({
      query: ({ id, ...body }) => ({
        url:    `/payments/admin/${id}/refund`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ParentPayments" as const, id },
        { type: "ParentPayments" as const, id: "ADMIN_LIST" },
      ],
    }),
  }),
});

export const {
  useInitializePaymentMutation,
  useVerifyPaymentMutation,
  useGetPaymentsQuery,
  useGetPaymentQuery,
  useAdminGetPaymentsQuery,
  useAdminGetPaymentQuery,
  useAdminConfirmPaymentMutation,
  useAdminRefundPaymentMutation,
} = paymentApi;